import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type Effect =
  | 'tap'
  | 'place'
  | 'vanish'
  | 'win'
  | 'lose'
  | 'tick'
  | 'react'
  | 'seat'
  | 'slide'
  | 'thud'
  | 'splash'
  | 'door'
  | 'countdown'
  | 'go'
  | 'caught'
  | 'key'
  | 'cheer'
  | 'draw'
  | 'turn'
  | 'crash'
  | 'hit'
  | 'hurt'
  | 'coin'
  | 'descend'
  | 'levelUp'
  | 'spell'
  | 'frost'

interface Settings {
  sound: boolean
  haptics: boolean
  toggleSound: () => void
  toggleHaptics: () => void
}

export const useSettings = create<Settings>()(
  persist(
    (set) => ({
      sound: true,
      haptics: true,
      toggleSound: () => set((s) => ({ sound: !s.sound })),
      toggleHaptics: () => set((s) => ({ haptics: !s.haptics })),
    }),
    { name: 'xox-settings' },
  ),
)

let ctx: AudioContext | null = null

function audio(): AudioContext | null {
  if (typeof window === 'undefined' || !('AudioContext' in window)) return null
  ctx ??= new AudioContext()
  if (ctx.state === 'suspended') void ctx.resume()
  return ctx
}

/** Unlock audio on the first user gesture (browsers block autoplaying contexts). */
export function primeAudio() {
  audio()
}

interface Tone {
  freq: number
  to?: number
  at: number
  dur: number
  type?: OscillatorType
  gain?: number
}

function tones(list: Tone[]) {
  const ac = audio()
  if (!ac) return
  const t0 = ac.currentTime
  for (const t of list) {
    const osc = ac.createOscillator()
    const g = ac.createGain()
    osc.type = t.type ?? 'sine'
    osc.frequency.setValueAtTime(t.freq, t0 + t.at)
    if (t.to) osc.frequency.exponentialRampToValueAtTime(t.to, t0 + t.at + t.dur)
    const peak = t.gain ?? 0.12
    g.gain.setValueAtTime(0.0001, t0 + t.at)
    g.gain.exponentialRampToValueAtTime(peak, t0 + t.at + 0.008)
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + t.at + t.dur)
    osc.connect(g).connect(ac.destination)
    osc.start(t0 + t.at)
    osc.stop(t0 + t.at + t.dur + 0.02)
  }
}

/** Short burst of filtered noise — the "shhk" of a puck sliding on ice. */
function noise(dur: number, gain: number, cutoffFrom: number, cutoffTo: number) {
  const ac = audio()
  if (!ac) return
  const frames = Math.floor(ac.sampleRate * dur)
  const buffer = ac.createBuffer(1, frames, ac.sampleRate)
  const data = buffer.getChannelData(0)
  for (let i = 0; i < frames; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / frames)
  const src = ac.createBufferSource()
  src.buffer = buffer
  const filter = ac.createBiquadFilter()
  filter.type = 'bandpass'
  filter.Q.value = 0.8
  filter.frequency.setValueAtTime(cutoffFrom, ac.currentTime)
  filter.frequency.exponentialRampToValueAtTime(cutoffTo, ac.currentTime + dur)
  const g = ac.createGain()
  g.gain.setValueAtTime(gain, ac.currentTime)
  g.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + dur)
  src.connect(filter).connect(g).connect(ac.destination)
  src.start()
}

const SOUNDS: Record<Effect, () => void> = {
  tap: () => tones([{ freq: 660, at: 0, dur: 0.05, type: 'triangle', gain: 0.06 }]),
  place: () => tones([{ freq: 520, to: 620, at: 0, dur: 0.09, type: 'triangle' }]),
  vanish: () => tones([{ freq: 700, to: 180, at: 0, dur: 0.28, type: 'sine', gain: 0.09 }]),
  win: () =>
    tones([
      { freq: 523, at: 0, dur: 0.14 },
      { freq: 659, at: 0.12, dur: 0.14 },
      { freq: 784, at: 0.24, dur: 0.14 },
      { freq: 1046, at: 0.36, dur: 0.32, gain: 0.14 },
    ]),
  lose: () =>
    tones([
      { freq: 330, at: 0, dur: 0.22, type: 'triangle' },
      { freq: 247, at: 0.2, dur: 0.38, type: 'triangle', gain: 0.1 },
    ]),
  tick: () => tones([{ freq: 1200, at: 0, dur: 0.035, type: 'square', gain: 0.035 }]),
  react: () => tones([{ freq: 640, to: 960, at: 0, dur: 0.08, type: 'triangle', gain: 0.07 }]),
  seat: () =>
    tones([
      { freq: 587, at: 0, dur: 0.1 },
      { freq: 880, at: 0.09, dur: 0.16, gain: 0.1 },
    ]),
  slide: () => noise(0.22, 0.18, 2400, 600),
  thud: () => tones([{ freq: 140, to: 70, at: 0, dur: 0.09, type: 'square', gain: 0.09 }]),
  splash: () => {
    noise(0.3, 0.14, 900, 200)
    tones([{ freq: 520, to: 110, at: 0, dur: 0.3, type: 'sine', gain: 0.1 }])
  },
  door: () =>
    tones([
      { freq: 880, at: 0, dur: 0.16 },
      { freq: 1320, at: 0.1, dur: 0.3, gain: 0.12 },
    ]),
  countdown: () => tones([{ freq: 660, at: 0, dur: 0.09, type: 'triangle', gain: 0.09 }]),
  go: () =>
    tones([
      { freq: 990, at: 0, dur: 0.12, type: 'triangle' },
      { freq: 1320, at: 0.08, dur: 0.28, type: 'triangle', gain: 0.13 },
    ]),
  caught: () => {
    noise(0.35, 0.16, 400, 120)
    tones([{ freq: 180, to: 60, at: 0, dur: 0.4, type: 'sawtooth', gain: 0.1 }])
  },
  key: () =>
    tones([
      { freq: 1046, at: 0, dur: 0.08, type: 'triangle', gain: 0.08 },
      { freq: 1568, at: 0.07, dur: 0.16, type: 'triangle', gain: 0.1 },
    ]),
  /** Neutral two-note shrug for a declared draw. */
  draw: () =>
    tones([
      { freq: 440, at: 0, dur: 0.16, type: 'triangle', gain: 0.08 },
      { freq: 392, at: 0.16, dur: 0.3, type: 'triangle', gain: 0.08 },
    ]),
  /** A light cycle turning: a short, bright blip. */
  turn: () => tones([{ freq: 880, to: 1100, at: 0, dur: 0.04, type: 'square', gain: 0.035 }]),
  /** A light cycle crashing into a wall: a low thump under a burst of static. */
  crash: () => {
    noise(0.22, 0.16, 2400, 300)
    tones([{ freq: 160, to: 45, at: 0, dur: 0.3, type: 'sawtooth', gain: 0.12 }])
  },
  /** Dungeon: a sword landing. */
  hit: () => {
    noise(0.06, 0.1, 3000, 900)
    tones([{ freq: 300, to: 180, at: 0, dur: 0.08, type: 'square', gain: 0.05 }])
  },
  /** Dungeon: the hero taking damage. */
  hurt: () => tones([{ freq: 220, to: 90, at: 0, dur: 0.22, type: 'sawtooth', gain: 0.09 }]),
  /** Dungeon: coins. */
  coin: () =>
    tones([
      { freq: 1320, at: 0, dur: 0.06, type: 'triangle', gain: 0.06 },
      { freq: 1760, at: 0.06, dur: 0.12, type: 'triangle', gain: 0.06 },
    ]),
  /** Dungeon: stairs down, a descending rumble. */
  descend: () => {
    noise(0.4, 0.08, 800, 120)
    tones([{ freq: 200, to: 70, at: 0, dur: 0.45, type: 'sine', gain: 0.08 }])
  },
  /** Account or hero level up: a rising fanfare. */
  levelUp: () =>
    tones([
      { freq: 523, at: 0, dur: 0.1, type: 'triangle', gain: 0.08 },
      { freq: 659, at: 0.1, dur: 0.1, type: 'triangle', gain: 0.08 },
      { freq: 784, at: 0.2, dur: 0.1, type: 'triangle', gain: 0.08 },
      { freq: 1047, at: 0.3, dur: 0.3, type: 'triangle', gain: 0.1 },
    ]),
  /** Dungeon: a scroll or knife. */
  spell: () => tones([{ freq: 600, to: 1400, at: 0, dur: 0.18, type: 'sine', gain: 0.07 }]),
  /** Dungeon: frost, a glassy high shimmer. */
  frost: () =>
    tones([
      { freq: 1800, at: 0, dur: 0.12, type: 'sine', gain: 0.05 },
      { freq: 2400, at: 0.05, dur: 0.2, type: 'sine', gain: 0.04 },
    ]),
  /** Crowd swell under the podium: a noise wash plus a rising major triad. */
  cheer: () => {
    noise(0.9, 0.1, 300, 1800)
    tones([
      { freq: 392, at: 0.05, dur: 0.25, type: 'triangle', gain: 0.07 },
      { freq: 494, at: 0.2, dur: 0.25, type: 'triangle', gain: 0.08 },
      { freq: 587, at: 0.35, dur: 0.45, type: 'triangle', gain: 0.1 },
    ])
  },
}

const HAPTICS: Partial<Record<Effect, number | number[]>> = {
  tap: 10,
  vanish: [12, 40, 18],
  win: [30, 60, 30, 60, 90],
  lose: 80,
  react: 8,
  seat: 20,
  slide: 8,
  thud: 15,
  splash: [20, 40, 30],
  door: [30, 60, 30],
  go: 40,
  caught: [40, 30, 60],
  key: 12,
  cheer: [30, 40, 60],
  draw: 25,
  turn: 6,
  crash: [60, 40, 90],
  hit: 10,
  hurt: 40,
  coin: 8,
  descend: [20, 30, 20],
  levelUp: [30, 40, 30, 80],
}

function reducedMotion(): boolean {
  return (
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

export function feedback(effect: Effect) {
  const { sound, haptics } = useSettings.getState()
  if (sound) {
    try {
      SOUNDS[effect]()
    } catch {
      // audio unavailable (no gesture yet, unsupported) — silent is fine
    }
  }
  const pattern = HAPTICS[effect]
  if (haptics && pattern && 'vibrate' in navigator && !reducedMotion()) {
    navigator.vibrate(pattern)
  }
}
